import { useState } from "react";
import { Link } from "react-router-dom";
import { 
  ArrowRight, 
  ArrowLeft, 
  CheckCircle, 
  User,
  Mail,
  Phone,
  MapPin,
  Briefcase,
  FileText,
  Heart,
  Users,
  Award,
  Loader2,
  UserPlus
} from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Section Components
const WelcomeSection = ({ onNext }) => (
  <div className="flex flex-col items-center justify-center min-h-[60vh] text-center animate-fade-in">
    <div className="w-20 h-20 bg-[#2bb673]/20 rounded-full flex items-center justify-center mb-6">
      <UserPlus className="w-10 h-10 text-[#2bb673]" />
    </div>
    <h1 className="font-serif text-4xl sm:text-5xl text-[#132238] mb-4">
      Nominate a JamBassador – 2026
    </h1>
    <p className="font-sans text-lg text-[#4b5563] max-w-lg mb-6">
      Recognizing Jamaicans across the diaspora who are making a meaningful impact.
    </p>
    <div className="bg-[#e9f8f0] border border-[#2bb673]/30 rounded-xl p-6 max-w-lg mb-8">
      <p className="font-sans text-[#16643d]">
        Do you know someone who is making a difference in <strong>business, music, arts, education, government, or community leadership</strong>?
      </p>
      <p className="font-sans text-[#16643d] mt-2">
        Nominate them to be considered as a JamBassador 2026.
      </p>
    </div>
    <button
      onClick={onNext}
      className="px-8 py-4 bg-[#132238] text-[#f2c94c] font-sans font-bold text-lg rounded-full hover:bg-[#1a2d4a] transition-all duration-300 shadow-xl flex items-center gap-3"
      data-testid="start-nomination-btn"
    >
      Start Nomination <ArrowRight size={20} />
    </button>
  </div>
);

const NominatorInfoSection = ({ data, onChange, errors }) => (
  <div className="animate-fade-in">
    <div className="flex items-center gap-3 mb-6">
      <div className="w-12 h-12 bg-[#0b3d91]/10 rounded-xl flex items-center justify-center">
        <User className="w-6 h-6 text-[#0b3d91]" />
      </div>
      <div>
        <h2 className="font-serif text-2xl text-[#132238]">Your Information</h2>
        <p className="font-sans text-sm text-[#4b5563]">Tell us about yourself</p>
      </div>
    </div>

    <div className="space-y-5">
      <div>
        <label className="block font-sans text-sm font-medium text-[#132238] mb-2">
          Your Full Name <span className="text-red-500">*</span>
        </label>
        <div className="relative">
          <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#4b5563]" />
          <input
            type="text"
            value={data.nominator_name}
            onChange={(e) => onChange("nominator_name", e.target.value)}
            className={`w-full pl-12 pr-4 py-3 border ${errors.nominator_name ? 'border-red-500' : 'border-gray-200'} rounded-xl font-sans focus:outline-none focus:ring-2 focus:ring-[#0b3d91]/20 focus:border-[#0b3d91]`}
            placeholder="Your full name"
            data-testid="input-nominator-name"
          />
        </div>
        {errors.nominator_name && <p className="text-red-500 text-sm mt-1">{errors.nominator_name}</p>}
      </div>

      <div>
        <label className="block font-sans text-sm font-medium text-[#132238] mb-2">
          Your Email Address <span className="text-red-500">*</span>
        </label>
        <div className="relative">
          <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#4b5563]" />
          <input
            type="email"
            value={data.nominator_email}
            onChange={(e) => onChange("nominator_email", e.target.value)}
            className={`w-full pl-12 pr-4 py-3 border ${errors.nominator_email ? 'border-red-500' : 'border-gray-200'} rounded-xl font-sans focus:outline-none focus:ring-2 focus:ring-[#0b3d91]/20 focus:border-[#0b3d91]`}
            placeholder="your@email.com"
            data-testid="input-nominator-email"
          />
        </div>
        {errors.nominator_email && <p className="text-red-500 text-sm mt-1">{errors.nominator_email}</p>}
      </div>

      <div>
        <label className="block font-sans text-sm font-medium text-[#132238] mb-2">
          Your Phone Number (optional)
        </label>
        <div className="relative">
          <Phone className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#4b5563]" />
          <input
            type="tel"
            value={data.nominator_phone}
            onChange={(e) => onChange("nominator_phone", e.target.value)}
            className="w-full pl-12 pr-4 py-3 border border-gray-200 rounded-xl font-sans focus:outline-none focus:ring-2 focus:ring-[#0b3d91]/20 focus:border-[#0b3d91]"
            placeholder="(123) 456-7890"
            data-testid="input-nominator-phone"
          />
        </div>
      </div>

      <div>
        <label className="block font-sans text-sm font-medium text-[#132238] mb-2">
          Your Relationship to Nominee <span className="text-red-500">*</span>
        </label>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          {["Friend", "Colleague", "Family", "Professional Contact", "Other"].map((rel) => (
            <label key={rel} className={`flex items-center gap-3 p-3 border rounded-xl cursor-pointer transition-all ${data.relationship === rel ? 'border-[#0b3d91] bg-[#0b3d91]/5' : 'border-gray-200 hover:border-[#0b3d91]'}`}>
              <input
                type="radio"
                name="relationship"
                value={rel}
                checked={data.relationship === rel}
                onChange={(e) => onChange("relationship", e.target.value)}
                className="w-5 h-5 text-[#0b3d91] focus:ring-[#0b3d91]"
                data-testid={`radio-relationship-${rel.toLowerCase().replace(/\s+/g, '-')}`}
              />
              <span className="font-sans text-sm text-[#132238]">{rel}</span>
            </label>
          ))}
        </div>
        {errors.relationship && <p className="text-red-500 text-sm mt-1">{errors.relationship}</p>}
      </div>
    </div>
  </div>
);

const NomineeInfoSection = ({ data, onChange, errors }) => (
  <div className="animate-fade-in">
    <div className="flex items-center gap-3 mb-6">
      <div className="w-12 h-12 bg-[#0b3d91]/10 rounded-xl flex items-center justify-center">
        <Users className="w-6 h-6 text-[#0b3d91]" />
      </div>
      <div>
        <h2 className="font-serif text-2xl text-[#132238]">Nominee Information</h2>
        <p className="font-sans text-sm text-[#4b5563]">About the person you're nominating</p>
      </div>
    </div>

    <div className="space-y-5">
      <div>
        <label className="block font-sans text-sm font-medium text-[#132238] mb-2">
          Nominee Full Name <span className="text-red-500">*</span>
        </label>
        <div className="relative">
          <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#4b5563]" />
          <input
            type="text"
            value={data.nominee_name}
            onChange={(e) => onChange("nominee_name", e.target.value)}
            className={`w-full pl-12 pr-4 py-3 border ${errors.nominee_name ? 'border-red-500' : 'border-gray-200'} rounded-xl font-sans focus:outline-none focus:ring-2 focus:ring-[#0b3d91]/20 focus:border-[#0b3d91]`}
            placeholder="Nominee's full name"
            data-testid="input-nominee-name"
          />
        </div>
        {errors.nominee_name && <p className="text-red-500 text-sm mt-1">{errors.nominee_name}</p>}
      </div>

      <div>
        <label className="block font-sans text-sm font-medium text-[#132238] mb-2">
          Nominee Email Address <span className="text-red-500">*</span>
        </label>
        <div className="relative">
          <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#4b5563]" />
          <input
            type="email"
            value={data.nominee_email}
            onChange={(e) => onChange("nominee_email", e.target.value)}
            className={`w-full pl-12 pr-4 py-3 border ${errors.nominee_email ? 'border-red-500' : 'border-gray-200'} rounded-xl font-sans focus:outline-none focus:ring-2 focus:ring-[#0b3d91]/20 focus:border-[#0b3d91]`}
            placeholder="nominee@email.com"
            data-testid="input-nominee-email"
          />
        </div>
        <p className="text-sm text-[#2bb673] mt-1 flex items-center gap-1">
          <CheckCircle size={14} /> Important: The nominee will be notified at this email
        </p>
        {errors.nominee_email && <p className="text-red-500 text-sm mt-1">{errors.nominee_email}</p>}
      </div>

      <div>
        <label className="block font-sans text-sm font-medium text-[#132238] mb-2">
          Nominee Phone Number (optional)
        </label>
        <div className="relative">
          <Phone className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#4b5563]" />
          <input
            type="tel"
            value={data.nominee_phone}
            onChange={(e) => onChange("nominee_phone", e.target.value)}
            className="w-full pl-12 pr-4 py-3 border border-gray-200 rounded-xl font-sans focus:outline-none focus:ring-2 focus:ring-[#0b3d91]/20 focus:border-[#0b3d91]"
            placeholder="(123) 456-7890"
            data-testid="input-nominee-phone"
          />
        </div>
      </div>

      <div>
        <label className="block font-sans text-sm font-medium text-[#132238] mb-2">
          City & Country of Residence <span className="text-red-500">*</span>
        </label>
        <div className="relative">
          <MapPin className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#4b5563]" />
          <input
            type="text"
            value={data.nominee_location}
            onChange={(e) => onChange("nominee_location", e.target.value)}
            className={`w-full pl-12 pr-4 py-3 border ${errors.nominee_location ? 'border-red-500' : 'border-gray-200'} rounded-xl font-sans focus:outline-none focus:ring-2 focus:ring-[#0b3d91]/20 focus:border-[#0b3d91]`}
            placeholder="New York, USA"
            data-testid="input-nominee-location"
          />
        </div>
        {errors.nominee_location && <p className="text-red-500 text-sm mt-1">{errors.nominee_location}</p>}
      </div>
    </div>
  </div>
);

const NomineeProfileSection = ({ data, onChange, errors }) => (
  <div className="animate-fade-in">
    <div className="flex items-center gap-3 mb-6">
      <div className="w-12 h-12 bg-[#0b3d91]/10 rounded-xl flex items-center justify-center">
        <Award className="w-6 h-6 text-[#0b3d91]" />
      </div>
      <div>
        <h2 className="font-serif text-2xl text-[#132238]">Nominee Profile</h2>
        <p className="font-sans text-sm text-[#4b5563]">Their professional background</p>
      </div>
    </div>

    <div className="space-y-5">
      <div>
        <label className="block font-sans text-sm font-medium text-[#132238] mb-2">
          Area of Impact <span className="text-red-500">*</span>
        </label>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {["Business", "Music", "Arts", "Education", "Government", "Community Leadership"].map((area) => (
            <label key={area} className={`flex items-center gap-3 p-3 border rounded-xl cursor-pointer transition-all ${data.area_of_impact === area ? 'border-[#0b3d91] bg-[#0b3d91]/5' : 'border-gray-200 hover:border-[#0b3d91]'}`}>
              <input
                type="radio"
                name="area_of_impact"
                value={area}
                checked={data.area_of_impact === area}
                onChange={(e) => onChange("area_of_impact", e.target.value)}
                className="w-5 h-5 text-[#0b3d91] focus:ring-[#0b3d91]"
                data-testid={`radio-area-${area.toLowerCase().replace(/\s+/g, '-')}`}
              />
              <span className="font-sans text-sm text-[#132238]">{area}</span>
            </label>
          ))}
        </div>
        {errors.area_of_impact && <p className="text-red-500 text-sm mt-1">{errors.area_of_impact}</p>}
      </div>

      <div>
        <label className="block font-sans text-sm font-medium text-[#132238] mb-2">
          Nominee's Current Role / Title <span className="text-red-500">*</span>
        </label>
        <div className="relative">
          <Briefcase className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#4b5563]" />
          <input
            type="text"
            value={data.nominee_role}
            onChange={(e) => onChange("nominee_role", e.target.value)}
            className={`w-full pl-12 pr-4 py-3 border ${errors.nominee_role ? 'border-red-500' : 'border-gray-200'} rounded-xl font-sans focus:outline-none focus:ring-2 focus:ring-[#0b3d91]/20 focus:border-[#0b3d91]`}
            placeholder="CEO, Artist, Educator, etc."
            data-testid="input-nominee-role"
          />
        </div>
        {errors.nominee_role && <p className="text-red-500 text-sm mt-1">{errors.nominee_role}</p>}
      </div>

      <div>
        <label className="block font-sans text-sm font-medium text-[#132238] mb-2">
          Organization / Brand (if applicable)
        </label>
        <input
          type="text"
          value={data.nominee_organization}
          onChange={(e) => onChange("nominee_organization", e.target.value)}
          className="w-full px-4 py-3 border border-gray-200 rounded-xl font-sans focus:outline-none focus:ring-2 focus:ring-[#0b3d91]/20 focus:border-[#0b3d91]"
          placeholder="Company name or brand"
          data-testid="input-nominee-organization"
        />
      </div>
    </div>
  </div>
);

const WhyNominateSection = ({ data, onChange, errors }) => (
  <div className="animate-fade-in">
    <div className="flex items-center gap-3 mb-6">
      <div className="w-12 h-12 bg-[#0b3d91]/10 rounded-xl flex items-center justify-center">
        <FileText className="w-6 h-6 text-[#0b3d91]" />
      </div>
      <div>
        <h2 className="font-serif text-2xl text-[#132238]">Why They Should Be Nominated</h2>
        <p className="font-sans text-sm text-[#4b5563]">Core information for consideration</p>
      </div>
    </div>

    <div className="space-y-5">
      <div>
        <label className="block font-sans text-sm font-medium text-[#132238] mb-2">
          Why are you nominating this individual? (150–300 words) <span className="text-red-500">*</span>
        </label>
        <textarea
          value={data.why_nominating}
          onChange={(e) => onChange("why_nominating", e.target.value)}
          rows={5}
          className={`w-full px-4 py-3 border ${errors.why_nominating ? 'border-red-500' : 'border-gray-200'} rounded-xl font-sans focus:outline-none focus:ring-2 focus:ring-[#0b3d91]/20 focus:border-[#0b3d91] resize-none`}
          placeholder="Tell us why this person deserves to be recognized..."
          data-testid="textarea-why-nominating"
        />
        <p className="text-sm text-[#4b5563] mt-1">{data.why_nominating.split(/\s+/).filter(Boolean).length} words</p>
        {errors.why_nominating && <p className="text-red-500 text-sm mt-1">{errors.why_nominating}</p>}
      </div>

      <div>
        <label className="block font-sans text-sm font-medium text-[#132238] mb-2">
          What impact have they made in their field or community? <span className="text-red-500">*</span>
        </label>
        <textarea
          value={data.nominee_impact}
          onChange={(e) => onChange("nominee_impact", e.target.value)}
          rows={4}
          className={`w-full px-4 py-3 border ${errors.nominee_impact ? 'border-red-500' : 'border-gray-200'} rounded-xl font-sans focus:outline-none focus:ring-2 focus:ring-[#0b3d91]/20 focus:border-[#0b3d91] resize-none`}
          placeholder="Describe their tangible impact..."
          data-testid="textarea-nominee-impact"
        />
        {errors.nominee_impact && <p className="text-red-500 text-sm mt-1">{errors.nominee_impact}</p>}
      </div>

      <div>
        <label className="block font-sans text-sm font-medium text-[#132238] mb-2">
          List any notable achievements (awards, milestones, influence, etc.)
        </label>
        <textarea
          value={data.nominee_achievements}
          onChange={(e) => onChange("nominee_achievements", e.target.value)}
          rows={3}
          className="w-full px-4 py-3 border border-gray-200 rounded-xl font-sans focus:outline-none focus:ring-2 focus:ring-[#0b3d91]/20 focus:border-[#0b3d91] resize-none"
          placeholder="• Achievement 1&#10;• Achievement 2&#10;• Achievement 3"
          data-testid="textarea-nominee-achievements"
        />
      </div>
    </div>
  </div>
);

const AlignmentSection = ({ data, onChange, errors }) => (
  <div className="animate-fade-in">
    <div className="flex items-center gap-3 mb-6">
      <div className="w-12 h-12 bg-[#0b3d91]/10 rounded-xl flex items-center justify-center">
        <Heart className="w-6 h-6 text-[#0b3d91]" />
      </div>
      <div>
        <h2 className="font-serif text-2xl text-[#132238]">Alignment</h2>
        <p className="font-sans text-sm text-[#4b5563]">Cultural representation</p>
      </div>
    </div>

    <div className="space-y-5">
      <div>
        <label className="block font-sans text-sm font-medium text-[#132238] mb-2">
          How does this individual represent Jamaican excellence or culture? <span className="text-red-500">*</span>
        </label>
        <textarea
          value={data.jamaican_representation}
          onChange={(e) => onChange("jamaican_representation", e.target.value)}
          rows={4}
          className={`w-full px-4 py-3 border ${errors.jamaican_representation ? 'border-red-500' : 'border-gray-200'} rounded-xl font-sans focus:outline-none focus:ring-2 focus:ring-[#0b3d91]/20 focus:border-[#0b3d91] resize-none`}
          placeholder="Describe how they embody Jamaican excellence..."
          data-testid="textarea-jamaican-representation"
        />
        {errors.jamaican_representation && <p className="text-red-500 text-sm mt-1">{errors.jamaican_representation}</p>}
      </div>
    </div>
  </div>
);

const ConsentSection = ({ data, onChange, errors }) => (
  <div className="animate-fade-in">
    <div className="flex items-center gap-3 mb-6">
      <div className="w-12 h-12 bg-[#0b3d91]/10 rounded-xl flex items-center justify-center">
        <CheckCircle className="w-6 h-6 text-[#0b3d91]" />
      </div>
      <div>
        <h2 className="font-serif text-2xl text-[#132238]">Consent & Awareness</h2>
        <p className="font-sans text-sm text-[#4b5563]">Final confirmation</p>
      </div>
    </div>

    <div className="space-y-5">
      <div>
        <label className="block font-sans text-sm font-medium text-[#132238] mb-2">
          Do you have permission to nominate this individual? <span className="text-red-500">*</span>
        </label>
        <div className="space-y-2">
          {["Yes", "No"].map((option) => (
            <label key={option} className={`flex items-center gap-3 p-3 border rounded-xl cursor-pointer transition-all ${data.has_permission === option ? 'border-[#0b3d91] bg-[#0b3d91]/5' : 'border-gray-200 hover:border-[#0b3d91]'}`}>
              <input
                type="radio"
                name="has_permission"
                value={option}
                checked={data.has_permission === option}
                onChange={(e) => onChange("has_permission", e.target.value)}
                className="w-5 h-5 text-[#0b3d91] focus:ring-[#0b3d91]"
                data-testid={`radio-permission-${option.toLowerCase()}`}
              />
              <span className="font-sans text-[#132238]">{option}</span>
            </label>
          ))}
        </div>
        {errors.has_permission && <p className="text-red-500 text-sm mt-1">{errors.has_permission}</p>}
      </div>

      <div className="bg-gray-50 rounded-xl p-6">
        <p className="font-sans text-[#132238] mb-4">I understand that:</p>
        <ul className="space-y-2 font-sans text-[#4b5563]">
          <li className="flex items-start gap-2">
            <span className="text-[#0b3d91] mt-1">•</span>
            Submission does not guarantee selection
          </li>
          <li className="flex items-start gap-2">
            <span className="text-[#0b3d91] mt-1">•</span>
            The nominee will be contacted directly
          </li>
        </ul>
      </div>

      <label className={`flex items-start gap-3 p-4 border ${errors.agreed_to_terms ? 'border-red-500' : 'border-gray-200'} rounded-xl cursor-pointer hover:border-[#0b3d91] transition-colors`}>
        <input
          type="checkbox"
          checked={data.agreed_to_terms}
          onChange={(e) => onChange("agreed_to_terms", e.target.checked)}
          className="w-5 h-5 mt-0.5 text-[#0b3d91] focus:ring-[#0b3d91] rounded"
          data-testid="checkbox-agreed-terms"
        />
        <span className="font-sans text-[#132238]">
          I agree to the above terms
        </span>
      </label>
      {errors.agreed_to_terms && <p className="text-red-500 text-sm mt-2">{errors.agreed_to_terms}</p>}
    </div>
  </div>
);

const SuccessSection = () => (
  <div className="flex flex-col items-center justify-center min-h-[60vh] text-center animate-fade-in">
    <div className="w-20 h-20 bg-[#2bb673]/20 rounded-full flex items-center justify-center mb-6">
      <CheckCircle className="w-10 h-10 text-[#2bb673]" />
    </div>
    <h1 className="font-serif text-3xl sm:text-4xl text-[#132238] mb-4">
      Thank You for Your Nomination!
    </h1>
    <p className="font-sans text-lg text-[#4b5563] max-w-lg mb-6">
      If selected, the nominee will be invited to apply for JamBassadors 2026.
    </p>
    <div className="bg-[#e9f8f0] border border-[#2bb673]/30 rounded-xl p-4 max-w-md mb-8">
      <p className="font-sans text-sm text-[#16643d]">
        <strong>What's Next?</strong> The nominee will receive an email notification about their nomination with instructions on how to apply.
      </p>
    </div>
    <Link
      to="/"
      className="inline-flex items-center gap-2 px-6 py-3 bg-[#0b3d91] text-white font-sans font-semibold rounded-full hover:bg-[#0a3580] transition-colors"
      data-testid="btn-return-home"
    >
      Return Home <ArrowRight size={18} />
    </Link>
  </div>
);

const NominationForm = () => {
  const [currentStep, setCurrentStep] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const [errors, setErrors] = useState({});
  
  // Form data
  const [nominatorInfo, setNominatorInfo] = useState({
    nominator_name: "",
    nominator_email: "",
    nominator_phone: "",
    relationship: ""
  });
  
  const [nomineeInfo, setNomineeInfo] = useState({
    nominee_name: "",
    nominee_email: "",
    nominee_phone: "",
    nominee_location: ""
  });
  
  const [nomineeProfile, setNomineeProfile] = useState({
    area_of_impact: "",
    nominee_role: "",
    nominee_organization: ""
  });
  
  const [whyNominate, setWhyNominate] = useState({
    why_nominating: "",
    nominee_impact: "",
    nominee_achievements: ""
  });
  
  const [alignment, setAlignment] = useState({
    jamaican_representation: ""
  });
  
  const [consent, setConsent] = useState({
    has_permission: "",
    agreed_to_terms: false
  });

  const steps = [
    { name: "Welcome", component: "welcome" },
    { name: "Your Info", component: "nominator" },
    { name: "Nominee Info", component: "nominee_info" },
    { name: "Profile", component: "nominee_profile" },
    { name: "Why", component: "why_nominate" },
    { name: "Alignment", component: "alignment" },
    { name: "Consent", component: "consent" }
  ];

  const validateStep = (step) => {
    const newErrors = {};
    
    switch (step) {
      case 1: // Nominator Info
        if (!nominatorInfo.nominator_name.trim()) newErrors.nominator_name = "Your name is required";
        if (!nominatorInfo.nominator_email.trim()) newErrors.nominator_email = "Your email is required";
        else if (!/\S+@\S+\.\S+/.test(nominatorInfo.nominator_email)) newErrors.nominator_email = "Invalid email format";
        if (!nominatorInfo.relationship) newErrors.relationship = "Please select your relationship";
        break;
      case 2: // Nominee Info
        if (!nomineeInfo.nominee_name.trim()) newErrors.nominee_name = "Nominee name is required";
        if (!nomineeInfo.nominee_email.trim()) newErrors.nominee_email = "Nominee email is required";
        else if (!/\S+@\S+\.\S+/.test(nomineeInfo.nominee_email)) newErrors.nominee_email = "Invalid email format";
        if (!nomineeInfo.nominee_location.trim()) newErrors.nominee_location = "Location is required";
        break;
      case 3: // Nominee Profile
        if (!nomineeProfile.area_of_impact) newErrors.area_of_impact = "Please select an area of impact";
        if (!nomineeProfile.nominee_role.trim()) newErrors.nominee_role = "Nominee's role is required";
        break;
      case 4: // Why Nominate
        if (!whyNominate.why_nominating.trim()) newErrors.why_nominating = "This field is required";
        if (!whyNominate.nominee_impact.trim()) newErrors.nominee_impact = "This field is required";
        break;
      case 5: // Alignment
        if (!alignment.jamaican_representation.trim()) newErrors.jamaican_representation = "This field is required";
        break;
      case 6: // Consent
        if (!consent.has_permission) newErrors.has_permission = "Please select an option";
        if (!consent.agreed_to_terms) newErrors.agreed_to_terms = "You must agree to the terms";
        break;
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (currentStep > 0 && !validateStep(currentStep)) {
      return;
    }
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
      setErrors({});
    }
  };

  const handlePrev = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
      setErrors({});
    }
  };

  const handleSubmit = async () => {
    if (!validateStep(currentStep)) {
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      const nominationData = {
        nominator: nominatorInfo,
        nominee: nomineeInfo,
        profile: nomineeProfile,
        why_nominate: whyNominate,
        alignment: alignment,
        consent: {
          has_permission: consent.has_permission === "Yes",
          agreed_to_terms: consent.agreed_to_terms
        }
      };
      
      await axios.post(`${API}/nominations`, nominationData);
      setIsComplete(true);
      
    } catch (error) {
      console.error("Submission error:", error);
      setErrors({ submit: error.response?.data?.detail || "Failed to submit nomination. Please try again." });
      setIsSubmitting(false);
    }
  };

  if (isComplete) {
    return (
      <div className="min-h-screen bg-[#f4f7fb]">
        <header className="bg-white border-b border-gray-100 sticky top-0 z-50">
          <div className="max-w-3xl mx-auto px-4 py-4">
            <Link to="/" className="flex items-center" data-testid="form-logo-link">
              <img 
                src="https://customer-assets-jai6qajn.emergentagent.net/job_jambassador-landing/artifacts/91dtn44o_jambassador-standard.png" 
                alt="JamBassador Awards" 
                className="h-16 md:h-20 w-auto"
              />
            </Link>
          </div>
        </header>
        <main className="max-w-2xl mx-auto px-4 py-8">
          <SuccessSection />
        </main>
      </div>
    );
  }

  const renderCurrentStep = () => {
    switch (steps[currentStep].component) {
      case "welcome":
        return <WelcomeSection onNext={handleNext} />;
      case "nominator":
        return <NominatorInfoSection data={nominatorInfo} onChange={(key, value) => setNominatorInfo({...nominatorInfo, [key]: value})} errors={errors} />;
      case "nominee_info":
        return <NomineeInfoSection data={nomineeInfo} onChange={(key, value) => setNomineeInfo({...nomineeInfo, [key]: value})} errors={errors} />;
      case "nominee_profile":
        return <NomineeProfileSection data={nomineeProfile} onChange={(key, value) => setNomineeProfile({...nomineeProfile, [key]: value})} errors={errors} />;
      case "why_nominate":
        return <WhyNominateSection data={whyNominate} onChange={(key, value) => setWhyNominate({...whyNominate, [key]: value})} errors={errors} />;
      case "alignment":
        return <AlignmentSection data={alignment} onChange={(key, value) => setAlignment({...alignment, [key]: value})} errors={errors} />;
      case "consent":
        return <ConsentSection data={consent} onChange={(key, value) => setConsent({...consent, [key]: value})} errors={errors} />;
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-[#f4f7fb]">
      {/* Header */}
      <header className="bg-white border-b border-gray-100 sticky top-0 z-50">
        <div className="max-w-3xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center" data-testid="form-logo-link">
            <img 
              src="https://customer-assets-jai6qajn.emergentagent.net/job_jambassador-landing/artifacts/91dtn44o_jambassador-standard.png" 
              alt="JamBassador Awards" 
              className="h-12 w-auto"
            />
          </Link>
          {currentStep > 0 && (
            <div className="flex items-center gap-2">
              <span className="font-sans text-sm text-[#4b5563]">Step {currentStep} of {steps.length - 1}</span>
              <div className="w-24 h-2 bg-gray-200 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-[#2bb673] transition-all duration-300"
                  style={{ width: `${(currentStep / (steps.length - 1)) * 100}%` }}
                />
              </div>
            </div>
          )}
        </div>
      </header>

      {/* Form Content */}
      <main className="max-w-2xl mx-auto px-4 py-8">
        {errors.submit && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl mb-6">
            {errors.submit}
          </div>
        )}
        
        {renderCurrentStep()}

        {/* Navigation Buttons */}
        {currentStep > 0 && (
          <div className="flex justify-between mt-8 pt-6 border-t border-gray-200">
            <button
              onClick={handlePrev}
              className="flex items-center gap-2 px-6 py-3 font-sans font-medium text-[#4b5563] hover:text-[#132238] transition-colors"
              data-testid="btn-prev"
            >
              <ArrowLeft size={18} /> Back
            </button>
            
            {currentStep < steps.length - 1 ? (
              <button
                onClick={handleNext}
                className="flex items-center gap-2 px-8 py-3 bg-[#0b3d91] text-white font-sans font-semibold rounded-full hover:bg-[#0a3580] transition-colors"
                data-testid="btn-next"
              >
                Continue <ArrowRight size={18} />
              </button>
            ) : (
              <button
                onClick={handleSubmit}
                disabled={isSubmitting}
                className="flex items-center gap-2 px-8 py-3 bg-[#2bb673] text-white font-sans font-bold rounded-full hover:bg-[#249e63] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="btn-submit-nomination"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 size={18} className="animate-spin" /> Submitting...
                  </>
                ) : (
                  <>
                    Submit Nomination <ArrowRight size={18} />
                  </>
                )}
              </button>
            )}
          </div>
        )}
      </main>
    </div>
  );
};

export default NominationForm;
